import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDataSubscription } from './queryKeys';

type ContactPointResponse = {
  code: number;
  message: string;
  requestId: UUID | undefined;
};

/**
 * Deletes a contact point.
 *
 * @param {UUID} contactPointId - An uuid containing the contact point details to be deleted.
 * @returns {Promise<ContactPointResponse>} - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Subscription/operation/delete_contact_point_data_subscription_contact_point__id__delete
 */
export const deleteContactPoint = async (contactPointId: UUID) => {
  const response = await vestigasApi
    .delete(ENDPOINT.DATA_SUBSCRIPTION.DELETE_CONTACT_POINT(contactPointId))
    .json<ContactPointResponse>();

  return response;
};

/**
 * Custom hook for deleting a contact point using mutations.
 *
 * @returns {Object} An object containing the mutation function and other properties.
 */
export const useMutationDeleteContactPoint = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contactPointId: UUID) =>
      deleteContactPoint(contactPointId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeysDataSubscription.getAllContactPoints(),
      });
    },
  });
};
